import * as DialogPrimitive from '@kobalte/core/dialog'
import type { PolymorphicProps } from '@kobalte/core/polymorphic'
import {
  type Component,
  type ComponentProps,
  type JSX,
  type JSXElement,
  splitProps,
  type ValidComponent,
} from 'solid-js'
import { cn } from '~/lib/utils'

export const Dialog = DialogPrimitive.Dialog

type DialogOverlayProps<T extends ValidComponent = 'div'> =
  DialogPrimitive.DialogOverlayProps<T> & { class?: string | undefined }

const DialogOverlay = <T extends ValidComponent = 'div'>(
  props: PolymorphicProps<T, DialogOverlayProps<T>>,
) => {
  const [, rest] = splitProps(props as DialogOverlayProps, ['class'])
  return (
    <DialogPrimitive.Overlay
      class={cn(
        'data-[expanded]:animate-in data-[closed]:animate-out',
        'peer data-[closed]:fade-out-0 data-[expanded]:fade-in-0 fixed inset-0 z-50 bg-[rgba(19,19,38,0.30)]',
        'backdrop-blur-md',
        props.class,
      )}
      {...rest}
    />
  )
}

type DialogContentProps<T extends ValidComponent = 'div'> =
  DialogPrimitive.DialogContentProps<T> & {
    class?: string | undefined
    children?: JSX.Element
  }

export const DialogTrigger = DialogPrimitive.Trigger

export const DialogContent = <T extends ValidComponent = 'div'>(
  props: PolymorphicProps<
    T,
    DialogContentProps<T> & {
      additionalLayer?: JSXElement
      overlayClass?: string
    }
  >,
) => {
  const [, rest] = splitProps(
    props as DialogContentProps & {
      additionalLayer?: JSXElement
      overlayClass?: string
    },
    ['class', 'children', 'additionalLayer', 'overlayClass'],
  )
  return (
    <DialogPrimitive.Portal>
      <DialogOverlay class={props.overlayClass}>
        {props.additionalLayer}
      </DialogOverlay>
      <DialogPrimitive.Content
        class={cn(
          'data-[expanded]:animate-in data-[closed]:animate-out data-[expanded]:fade-in-0 data-[closed]:fade-out-0 lock-transform fixed top-1/2 left-1/2 z-50',
          props.class,
        )}
        {...rest}
      >
        {props.children}
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  )
}

export const DialogHeader: Component<ComponentProps<'div'>> = (props) => {
  const [, rest] = splitProps(props, ['class'])
  return (
    <div
      class={cn(
        'flex flex-col space-y-1.5 text-center sm:text-left',
        props.class,
      )}
      {...rest}
    />
  )
}

type DialogTitleProps<T extends ValidComponent = 'h2'> =
  DialogPrimitive.DialogTitleProps<T> & {
    class?: string | undefined
  }

export const DialogTitle = <T extends ValidComponent = 'h2'>(
  props: PolymorphicProps<T, DialogTitleProps<T>>,
) => {
  const [, rest] = splitProps(props as DialogTitleProps, ['class'])
  return (
    <DialogPrimitive.Title
      class={cn('text-xl leading-none font-medium', props.class)}
      {...rest}
    />
  )
}
